import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import app, { createReducer } from './reducers';
import FacetedSearch from './Components/FacetedSearch';
import FacetedSearchCompactContainer from './Components/FacetedSearchCompactContainer';
import DynamicComponent from './Components/DynamicComponent';
import VanillaEvents from './Components/VanillaEvents';
import './Containers/Vanilla.container';
import { showLogin } from './Actions/Login.action';
import { ReadProductCardAttributes } from './Components/ProductCard/ProductCardUtils';
import './Server/Redux.server';
import ProductSizeSelector from './Components/ProductSizeSelector/ProductSizeSelector';

ReduxServer.createStore(global.LitiumPreloadState);

const hydrateServerComponents = () => {
  import('./Server/expose-components').then(() => {
    const hydrationScriptTag = document.getElementById('ssr-hydration-script');
    const newTag = document.createElement('script');
    newTag.innerText = hydrationScriptTag.innerText;
    document.body.removeChild(hydrationScriptTag);
    document.body.appendChild(newTag);
    newTag.remove();
  });
};

hydrateServerComponents();

// Add a dictionary to keep track of the registered async reducers
global.LitiumReduxStore.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
global.LitiumReduxStore.injectReducer = (key, asyncReducer) => {
  if (!global.LitiumReduxStore.asyncReducers[key]) {
    global.LitiumReduxStore.asyncReducers[key] = asyncReducer;
    global.LitiumReduxStore.replaceReducer(
      createReducer(global.LitiumReduxStore.asyncReducers)
    );
  }
};

window.__litium = {
  ...window.__litium,
  bootstrapComponents: () => {
    // bootstrap React components, in case the HTML response we receive from the server
    // has React components. ReactDOM.render performs only an update on previous rendered
    // components and only mutate the DOM as necessary to reflect latest React element.
    bootstrapComponents();
  },
  cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
  registeredContainers.push(container);
  ReactDOM.render(element, container, callback);
};

window.onunload = () => {
  // make sure components are unmounted, redux's listener are unsubscribed
  // to fix issue with iframe navigation in IE
  registeredContainers.forEach((container) => {
    ReactDOM.unmountComponentAtNode(container);
  });
};

const bootstrapComponents = () => {
  if (document.getElementById('miniCart')) {
    const MiniCart = DynamicComponent({
      loader: () => import('./Components/MiniCart/MiniCart'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <MiniCart />
      </Provider>,
      document.getElementById('miniCart')
    );
  }
  if (document.getElementById('quickSearch')) {
    const QuickSearch = DynamicComponent({
      loader: () => import('./Components/QuickSearch/QuickSearch'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <QuickSearch />
      </Provider>,
      document.getElementById('quickSearch')
    );
  }
  if (document.getElementById('quickSearchController')) {
    const QuickSearchController = DynamicComponent({
      loader: () => import('./Components/QuickSearch/QuickSearchController'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <QuickSearchController />
      </Provider>,
      document.getElementById('quickSearchController')
    );
  }
  if (document.getElementById('navbar')) {
    const NavigationContainer = DynamicComponent({
      loader: () => import('./Components/Navigation/NavigationContainer'),
    });
    const Navigation = DynamicComponent({
      loader: () => import('./Components/Navigation/Navigation'),
    });

    const blockElementsContainer = document.getElementById('mega-menu-blocks');
    const blockElements = blockElementsContainer
      ? Array.from(blockElementsContainer.children)
      : [];

    if (blockElementsContainer) blockElementsContainer.remove();

    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <NavigationContainer blockElements={blockElements} />
      </Provider>,
      document.getElementById('navbar')
    );
  }

  const navigationMobileTrigger = document.getElementById(
    'navigation-mobile-trigger'
  );
  if (navigationMobileTrigger) {
    const NavigationMobileTrigger = DynamicComponent({
      loader: () =>
        import('./Components/NavigationMobile/NavigationMobileTrigger'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <NavigationMobileTrigger element={navigationMobileTrigger} />
      </Provider>,
      navigationMobileTrigger
    );
  }

  const categorySubnavigations = document.querySelectorAll(
    '.category-subnavigation'
  );
  if (categorySubnavigations.length > 0) {
    const CategorySubnavigation = DynamicComponent({
      loader: () =>
        import('./Components/CategorySubnavigation/CategorySubnavigation'),
    });

    categorySubnavigations.forEach((elm) => {
      let items = Array.from(elm.children).map((e) => ({
        title: e.dataset['title'],
        url: e.dataset['url'],
      }));

      renderReact(
        <Provider store={global.LitiumReduxStore}>
          <CategorySubnavigation items={items} />
        </Provider>,
        elm
      );
    });
  }

  if (document.getElementById('filterbar')) {
    const FilterBar = DynamicComponent({
      loader: () => import('./Components/Filterbar/FilterBar'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <FilterBar />
      </Provider>,
      document.getElementById('filterbar')
    );
  }

  if (document.getElementById('facetedSearch')) {
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <FacetedSearch />
      </Provider>,
      document.getElementById('facetedSearch')
    );
  }
  if (document.getElementById('facetedSearchCompact')) {
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <FacetedSearchCompactContainer />
      </Provider>,
      document.getElementById('facetedSearchCompact')
    );
  }
  if (document.getElementById('myPagePersons')) {
    const PersonList = DynamicComponent({
      loader: () => import('./Components/PersonListContainer'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <PersonList />
      </Provider>,
      document.getElementById('myPagePersons')
    );
  }
  if (document.getElementById('myPageAddresses')) {
    const AddressList = DynamicComponent({
      loader: () => import('./Components/AddressListContainer'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <AddressList />
      </Provider>,
      document.getElementById('myPageAddresses')
    );
  }

  const sizeFilter = document.getElementById('sizefilter');
  if (sizeFilter) {
    const ProductSizeSelector = DynamicComponent({
      loader: () =>
        import('./Components/ProductSizeSelector/ProductSizeSelector'),
    });
    const sizes = JSON.parse(sizeFilter.dataset.sizes);

    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <ProductSizeSelector sizes={sizes} />
      </Provider>,
      sizeFilter
    );
  }

  if (document.getElementById('checkout')) {
    const Checkout = DynamicComponent({
      loader: () => import('./Components/Checkout'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <Checkout />
      </Provider>,
      document.getElementById('checkout')
    );
  }
  if (document.getElementById('lightBoxImages')) {
    const LightboxImages = DynamicComponent({
      loader: () => import('./Components/LightboxImages'),
    });
    import('./Reducers/LightboxImages.reducer').then(({ lightboxImages }) => {
      global.LitiumReduxStore.injectReducer('lightboxImages', lightboxImages);
      const rootElement = document.getElementById('lightBoxImages');
      const images = Array.from(rootElement.querySelectorAll('template')).map(
        (img) => ({
          html: img.innerHTML,
          src: img.dataset.src,
        })
      );
      renderReact(
        <Provider store={global.LitiumReduxStore}>
          <LightboxImages images={images} />
        </Provider>,
        document.getElementById('lightBoxImages')
      );
    });
  }

  const productImageContainers = document.querySelectorAll('product-images');
  if (productImageContainers.length > 0) {
    const ProductImageContainer = DynamicComponent({
      loader: () => import('./Components/ProductImage/ProductImageContainer'),
    });
    Array.from(productImageContainers).forEach((container) => {
      const productImages = Array.from(
        container.querySelectorAll(':scope > product-image')
      ).map((productImage) => {
        const {
          originalUrl,
          thumbnailUrl,
          slideUrl,
          alt,
        } = productImage.dataset;
        return {
          originalUrl,
          thumbnailUrl,
          slideUrl,
          alt,
        };
      });
      if (productImages.length > 0) {
        renderReact(
          <Provider store={global.LitiumReduxStore}>
            <ProductImageContainer {...{ productImages }} />
          </Provider>,
          container
        );
      }
    });
  }

  if (document.querySelectorAll('.slick-slider__wrapper').length > 0) {
    const SlickSlider = DynamicComponent({
      loader: () => import('./Components/SlickSlider/SlickSlider'),
    });
    Array.from(document.querySelectorAll('.slick-slider__wrapper')).forEach(
      (slider, index) => {
        const values = [...slider.querySelectorAll('template')].map((block) => {
          return {
            html: block.innerHTML,
          };
        });
        if (values.length > 0) {
          renderReact(<SlickSlider values={values} />, slider);
        }
      }
    );
  }

  const productCards = document.querySelectorAll(
    'product-card[data-type="default"], product-card[data-type="wide"]'
  );
  if (productCards.length > 0) {
    const ProductCard = DynamicComponent({
      loader: () => import('./Components/ProductCard/ProductCard'),
    });
    Array.from(productCards).forEach((productCard) => {
      const params = ReadProductCardAttributes(productCard);
      renderReact(
        <Provider store={global.LitiumReduxStore}>
          <ProductCard {...params} />
        </Provider>,
        productCard
      );
    });
  }

  const carousels = document.querySelectorAll('product-carousel');
  if (carousels.length > 0) {
    const ProductCarousel = DynamicComponent({
      loader: () => import('./Components/ProductCarousel/ProductCarousel'),
    });
    Array.from(carousels).forEach((carousel) => {
      const { title, url, urlTitle } = carousel.dataset;
      const elements = [
        ...carousel.querySelectorAll(
          '.product-carousel-block__product product-card[data-type="carousel"]'
        ),
      ].map(ReadProductCardAttributes);
      if (elements.length > 0) {
        renderReact(
          <Provider store={global.LitiumReduxStore}>
            <ProductCarousel
              {...{
                elements,
                title,
                url,
                urlTitle,
              }}
            />
          </Provider>,
          carousel
        );
      }
    });
  }

  const loginButtonElem = document.getElementById(
    'login-menu-trigger-container'
  );
  if (loginButtonElem) {
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <VanillaEvents
          selector="#login-menu-trigger"
          events={[
            {
              name: 'click',
              func: (e) => {
                e.preventDefault();
                global.LitiumReduxStore.dispatch(showLogin(true));
              },
            },
          ]}
        />
      </Provider>,
      loginButtonElem
    );
  }

  const loginContainer = document.getElementById('login-container');
  if (loginContainer) {
    const LoginModal = DynamicComponent({
      loader: () => import('./Components/LoginModal/LoginModal'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <LoginModal />
      </Provider>,
      loginContainer
    );
  }

  let overlayContainerElem = document.getElementById('overlayContainer');
  if (overlayContainerElem) {
    const OverlayContainer = DynamicComponent({
      loader: () => import('./Components/OverlayContainer'),
    });

    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <OverlayContainer />
      </Provider>,
      overlayContainerElem
    );
  }

  if (document.querySelectorAll('buy-button').length > 0) {
    const BuyButton = DynamicComponent({
      loader: () => import('./Components/BuyButton'),
    });
    Array.from(document.querySelectorAll('buy-button')).forEach((button) => {
      const {
        articleNumber,
        quantityFieldId,
        href,
        cssClass,
        label,
      } = button.dataset;
      renderReact(
        <Provider store={global.LitiumReduxStore}>
          <BuyButton
            {...{
              label,
              articleNumber,
              quantityFieldId,
              href,
              cssClass,
            }}
          />
        </Provider>,
        button
      );
    });
  }

  const buyButtonPacks = document.querySelectorAll('buy-button-pack');
  if (buyButtonPacks.length > 0) {
    const BuyButtonPack = DynamicComponent({
      loader: () => import('./Components/BuyButtonPack/BuyButtonPack'),
    });
    Array.from(buyButtonPacks).forEach((button) => {
      const {
        articleNumber,
        quantityFieldId,
        href,
        cssClass,
        label,
        packSize,
      } = button.dataset;
      renderReact(
        <Provider store={global.LitiumReduxStore}>
          <BuyButtonPack
            {...{
              label,
              articleNumber,
              quantityFieldId,
              href,
              cssClass,
              packSize: parseInt(packSize),
            }}
          />
        </Provider>,
        button
      );
    });
  }

  if (document.getElementById('my-pages-navigation')) {
    const MyPagesNavigation = DynamicComponent({
      loader: () => import('./Components/MyPages/MyPagesNavigation'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <MyPagesNavigation />
      </Provider>,
      document.getElementById('my-pages-navigation')
    );
  }
  if (document.getElementById('my-pages-tab-content')) {
    const MyPagesTabContainer = DynamicComponent({
      loader: () => import('./Components/MyPages/MyPagesTabContainer'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <MyPagesTabContainer />
      </Provider>,
      document.getElementById('my-pages-tab-content')
    );
  }

  if (document.getElementById('orderHistoryPage')) {
    const OrderList = DynamicComponent({
      loader: () => import('./Components/OrderHistoryListContainer'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <OrderList />
      </Provider>,
      document.getElementById('orderHistoryPage')
    );
  }

  const brandList = document.getElementById('brand-list');
  if (brandList) {
    const BrandListContainer = DynamicComponent({
      loader: () => import('./Components/BrandList/BrandListContainer'),
    });
    const brands = brandList.dataset['brands'];

    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <BrandListContainer brands={brands} />
      </Provider>,
      brandList
    );
  }

  const faqBlocks = document.querySelectorAll('faq-block');
  if (faqBlocks.length > 0) {
    const FaqContainer = DynamicComponent({
      loader: () => import('./Components/Faq/FaqContainer'),
    });
    Array.from(faqBlocks).forEach((faqBlock) => {
      const { type } = faqBlock.dataset;
      const items = Array.from(
        faqBlock.querySelectorAll(':scope > faq-block-item')
      ).map((faqBlockItem) => {
        const answer = faqBlockItem.innerHTML;
        const {
          question,
          icon,
          showMore,
          defaultExpanded,
          items,
        } = faqBlockItem.dataset;
        return {
          ...{
            question,
            answer,
            icon,
            items,
            showMore: showMore === 'true',
            defaultExpanded: defaultExpanded === 'true',
          },
        };
      });
      renderReact(
        <Provider store={global.LitiumReduxStore}>
          <FaqContainer
            {...{
              items,
              type,
            }}
          />
        </Provider>,
        faqBlock
      );
    });
  }

  const productStackedPrices = document.querySelectorAll(
    'product-stacked-prices'
  );
  if (productStackedPrices.length > 0) {
    const StackedPrices = DynamicComponent({
      loader: () => import('./Components/StackedPrices/StackedPrices'),
    });
    Array.from(productStackedPrices).forEach((productStackedPrice) => {
      const articleNumber = productStackedPrice.dataset.articleNumber;
      const tierPrices = JSON.parse(productStackedPrice.dataset.tierPrices);
      renderReact(
        <Provider store={global.LitiumReduxStore}>
          <StackedPrices
            {...{
              articleNumber,
              tierPrices,
            }}
          />
        </Provider>,
        productStackedPrice
      );
    });
  }

  if (document.getElementById('orderPrintPage')) {
    const OrderHistoryB2CDetailPrintContainer = DynamicComponent({
      loader: () =>
        import('./Components/MyPages/OrderHistoryB2CDetailPrintContainer'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <OrderHistoryB2CDetailPrintContainer print={true} />
      </Provider>,
      document.getElementById('orderPrintPage')
    );
  }

  const newsletterSignups = document.querySelectorAll('.news-letter-signup');
  if (newsletterSignups.length > 0) {
    const Newsletter = DynamicComponent({
      loader: () => import('./Components/NewsLetter/NewsLetterOptin'),
    });
    Array.from(newsletterSignups).forEach((newsletterSignup, index) => {
      renderReact(
        <Provider store={global.LitiumReduxStore}>
          <Newsletter
            title={newsletterSignup.dataset.title}
            text={newsletterSignup.dataset.text}
            termsurl={newsletterSignup.dataset.termsurl}
            termstext={newsletterSignup.dataset.termstext}
            placeholder={newsletterSignup.dataset.placeholder}
            buttontext={newsletterSignup.dataset.buttontext}
            tagstext={newsletterSignup.dataset.tagstext}
            number={index}
            tags={
              newsletterSignup.dataset.tags
                ? JSON.parse(newsletterSignup.dataset.tags)
                : {}
            }
          />
        </Provider>,
        newsletterSignup
      );
    });
  }

  const languageSwitcher = document.getElementById('language-switcher');
  if (languageSwitcher) {
    const MobileNavigationLanguageSwitcher = DynamicComponent({
      loader: () =>
        import(
          './Components/NavigationMobile/MobileNavigationLanguageSwitcher'
        ),
    });

    const languageItem = JSON.parse(languageSwitcher.dataset.item);
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <MobileNavigationLanguageSwitcher
          currentChannel={languageItem.currentChannel}
          otherChannels={languageItem.otherChannels}
        />
      </Provider>,
      languageSwitcher
    );
  }
};
bootstrapComponents();
